import { Controller } from "@hotwired/stimulus"

// sessionStorage key for the panel-local trade quick-filter (re-applied on connect so the
// selection survives Turbo re-renders and date navigation within the tab).
const TRADE_FILTER_KEY = "planning:panel-trade-filter"

// sessionStorage key for the panel-local Mitarbeitertyp quick-filter, kept like the trade one.
const TYPE_FILTER_KEY = "planning:panel-type-filter"

// The chip quick-filters above the employee cards (Planning::FilterChipRowComponent), keyed by the
// `filter` action param their chips carry. Each holds a Set of chip-id strings ("none" = employees
// without one; empty = no filter) in this.chipFilters and composes with the others.
const CHIP_FILTERS = {
  trade: { storageKey: TRADE_FILTER_KEY, target: "tradeChip" },
  type: { storageKey: TYPE_FILTER_KEY, target: "typeChip" },
}

// sessionStorage key for the proximity sort. Stores the full {siteId, siteName, lat, lng}
// payload so the sort survives re-renders even when the target site is no longer rendered
// on the board (the chip still names it and offers reset).
const PROXIMITY_SORT_KEY = "planning:panel-proximity-sort"

// sessionStorage key for the open/collapsed state. The panel starts collapsed by default so
// the board is visible immediately; once the user opens/closes it, that choice survives Turbo
// re-renders and date navigation within the session.
const PANEL_EXPANDED_KEY = "planning:panel-expanded"

// sessionStorage key for the multi-select set (employee ids). Persisted so the selection
// survives Turbo re-renders / date navigation until the user drops it or clears it.
const SELECTED_KEY = "planning:panel-selected"

// Ring applied to every card of a selected employee (an employee repeats across the per-day
// columns, so all their cards light up together).
const SELECTED_CLASSES = ["ring-2", "ring-bautask-green", "ring-offset-1"]

// Great-circle (Luftlinie) distance in km — mirrors AirDistanceCalculator (Ruby).
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const rad = (degrees) => (degrees * Math.PI) / 180
  const dLat = rad(lat2 - lat1)
  const dLng = rad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Collapsible bottom resource panel for the capacity-planning board. Besides toggling and
// filtering, it pixel-aligns its day columns with the board grid above: the panel is a
// position:fixed element spanning the viewport, while the board lives inside the scrollable
// <main>, so their widths/offsets differ (scrollbar, padding). Mirroring the board grid's
// measured geometry keeps the columns lined up regardless. Dragging is handled by the
// shared plantafel-drag controller on each card.
//
// The panes arrive later than the shell: they are fetched into the `frame` target on the first
// expand (contentUrl), so everything connect() applies to cards, chips and columns is applied
// again in contentLoaded().
export default class extends Controller {
  static targets = [
    "content",
    "expandIcon",
    "collapseIcon",
    "grid",
    "item",
    "column",
    "employeesPane",
    "tradesPane",
    "fleetsPane",
    "teamsPane",
    "employeesTab",
    "tradesTab",
    "fleetsTab",
    "teamsTab",
    "tradeItem",
    "fleetItem",
    "teamItem",
    "fleetSearch",
    "fleetTypeChip",
    "poolItem",
    "poolSearch",
    "tradeChip",
    "typeChip",
    "cardList",
    "proximityChip",
    "proximityChipName",
    "selectCheckbox",
    "selectionBar",
    "selectionCount",
    "plannedState",
    "frame",
  ]
  static values = {
    expanded: { type: Boolean, default: false },
    activeTab: { type: String, default: "employees" },
    // "columns" pixel-aligns per-day employee columns with the board grid; "pool" (4-week
    // view) shows a single reflowing employee pool and skips grid alignment entirely.
    layout: { type: String, default: "columns" },
    // "N {countLabel}" suffix for the selection summary (kept out of the JS so it stays
    // translatable), e.g. countLabel "ausgewählt" → "3 ausgewählt".
    countLabel: { type: String, default: "" },
    // BoardController#resource_panel for the window on screen; loaded on the first expand.
    contentUrl: { type: String, default: "" },
  }

  connect() {
    this.availableOnly = false
    this.unplannedOnly = false
    this.restoreExpanded()
    this.loadChipFilters()
    this.proximitySort = this.loadProximitySort()
    this.selectedIds = this.loadSelection()
    this.applyAllFilters()
    this.applyProximitySort()
    this.syncProximityUi()
    this.syncSelection()
    this.initialRender()
    this.renderTabs()

    this.boundSchedule = this.scheduleSync.bind(this)
    this.boundAdjustSpacer = this.adjustSpacer.bind(this)
    this.boundProximity = this.onProximityEvent.bind(this)
    this.boundClearSelection = this.clearSelection.bind(this)
    window.addEventListener("resize", this.boundSchedule)
    document.addEventListener("turbo:render", this.boundSchedule)
    document.addEventListener("planning:sort-by-proximity", this.boundProximity)
    document.addEventListener("planning:clear-selection", this.boundClearSelection)
    // Re-measure once the collapse/expand animation finishes.
    if (this.hasContentTarget) {
      this.contentTarget.addEventListener("transitionend", this.boundAdjustSpacer)
    }
    this.observeBoard()
    this.observeBoardWidth()
    this.scheduleSync()
    // A Turbo cache restore brings the panes back with the snapshot; otherwise fetch them now if
    // the panel was left open (the expanded state is restored above).
    if (this.contentPresent()) this.contentRequested = true
    else if (this.expandedValue) this.loadContent()
  }

  contentPresent() {
    return this.hasEmployeesPaneTarget || this.hasTradesPaneTarget
  }

  // Point the frame at the panes once; Turbo fetches them and fires turbo:frame-load.
  loadContent() {
    if (this.contentRequested || !this.hasFrameTarget || !this.contentUrlValue) return
    this.contentRequested = true
    if (this.contentPresent()) return
    this.frameTarget.src = this.contentUrlValue
  }

  // The panes have arrived: re-apply what connect() could not while they were missing. The
  // stored trade filter and selection are re-read (connect pruned them against no chips/cards,
  // in memory only), the proximity snapshot is rebuilt for the new card lists, and the
  // checkbox-backed filters restart unchecked like the checkboxes that just rendered.
  contentLoaded() {
    this.contentRequested = true
    this.availableOnly = false
    this.unplannedOnly = false
    this.fleetTypeFilter = "all"
    this.loadChipFilters()
    this.selectedIds = this.loadSelection()
    this.defaultOrders = new Map()
    this.applyAllFilters()
    this.applyProximitySort()
    this.syncProximityUi()
    this.syncSelection()
    this.renderTabs()
  }

  disconnect() {
    window.removeEventListener("resize", this.boundSchedule)
    document.removeEventListener("turbo:render", this.boundSchedule)
    document.removeEventListener("planning:sort-by-proximity", this.boundProximity)
    document.removeEventListener("planning:clear-selection", this.boundClearSelection)
    if (this.hasContentTarget) {
      this.contentTarget.removeEventListener("transitionend", this.boundAdjustSpacer)
    }
    this.boardObserver?.disconnect()
    this.widthObserver?.disconnect()
  }

  // Re-align when the board grid's width changes (e.g. the stats sidebar collapses/expands,
  // which resizes the board without firing a window resize or a board mutation).
  observeBoardWidth() {
    if (!window.ResizeObserver) return
    this.widthObserver = new ResizeObserver(this.boundSchedule)
    const boardGrid = document.querySelector("[data-planning-grid]")
    if (boardGrid) this.widthObserver.observe(boardGrid)
    // Also re-align when the visible table width changes (pool layout has no grid to watch).
    const scroll = document.querySelector("[data-planning-board-scroll]")
    if (scroll) this.widthObserver.observe(scroll)
  }

  // Re-align after the board grid (re-)renders, e.g. on prev/today/next navigation.
  observeBoard() {
    const board = document.getElementById("planning_board")
    const container = board?.parentElement || document.body
    this.boardObserver = new MutationObserver(this.boundSchedule)
    this.boardObserver.observe(container, { childList: true, subtree: true })
  }

  scheduleSync() {
    if (this.syncScheduled) return
    this.syncScheduled = true
    requestAnimationFrame(() => {
      this.syncScheduled = false
      this.syncAlignment()
      this.adjustSpacer()
      // Board re-renders (date navigation) replace the site rows — re-highlight the active
      // proximity button. Class toggles are attribute mutations, which the board observer
      // doesn't watch (childList only), so this can't loop.
      this.syncProximityUi()
    })
  }

  // The panel renders `invisible` (it's position:fixed spanning the full content width, so it
  // would otherwise paint over the stats sidebar, and its inner columns would show at their
  // unaligned natural width before snapping — a flicker). revealOnce() is called from
  // syncAlignment ONLY once the panel is fully aligned (outer box + inner grid), so it appears
  // already in its final geometry. Later syncs must not re-toggle visibility.
  revealOnce() {
    if (this.revealed) return
    this.revealed = true
    this.element.classList.remove("invisible")
  }

  // Keep the in-flow bottom spacer as tall as the fixed panel, so the last board section can
  // always be scrolled clear of it (the panel grows from ~header height to ~50vh when open).
  adjustSpacer() {
    const spacer = document.getElementById("planning_bottom_spacer")
    if (spacer) spacer.style.height = `${this.element.offsetHeight}px`
  }

  // Mirror the board table's geometry onto the panel: the whole panel box matches the
  // bordered table's left/right edges, and the inner day columns overlay the calendar
  // columns exactly. Keeps everything lined up regardless of nav width, page padding or
  // scrollbar (the panel is position:fixed while the board lives in the scrollable <main>).
  syncAlignment() {
    // Outer box: always align the panel to the VISIBLE part of the table (the horizontal
    // scroll container), so it spans exactly the table width and never extends under the
    // stats sidebar — in both the per-day-column and the 4-week pool layouts.
    const scroll = document.querySelector("[data-planning-board-scroll]")
    if (!scroll) return
    const scrollRect = scroll.getBoundingClientRect()
    this.element.style.left = `${scrollRect.left}px`
    this.element.style.right = "auto"
    this.element.style.width = `${scrollRect.width}px`

    // Pool layout (4-week view) has no per-day columns — its flex-wrap pool just fills the
    // panel, so there is nothing further to mirror; the panel is fully positioned now.
    if (this.layoutValue === "pool" || !this.hasGridTarget) {
      this.revealOnce()
      return
    }

    // Columns layout: stay hidden until the board grid has rendered, so we never reveal the
    // panel with its inner columns at their unaligned natural width (a visible snap). The
    // board mutating in triggers another sync via the MutationObserver, which reveals then.
    const boardGrid = document.querySelector("[data-planning-grid]")
    if (!boardGrid) return

    // Inner columns: copy the board grid's resolved tracks and horizontal box.
    const boardRect = boardGrid.getBoundingClientRect()
    const grid = this.gridTarget
    grid.style.gridTemplateColumns = getComputedStyle(boardGrid).gridTemplateColumns
    grid.style.width = `${boardRect.width}px`
    grid.style.marginLeft = "0px"
    grid.style.marginLeft = `${boardRect.left - grid.getBoundingClientRect().left}px`

    // Outer box and inner columns are now aligned — safe to reveal.
    this.revealOnce()
  }

  toggle(event) {
    // Tab buttons live inside the clickable header — don't collapse the panel when one
    // is pressed (switchTab handles the activation).
    if (event?.target?.closest("button")) return
    this.expandedValue = !this.expandedValue
  }

  switchTab(event) {
    event.stopPropagation()
    const tab = event.currentTarget.dataset.tab
    this.activeTabValue = tab
    // Make sure the panel is open when the user switches tabs.
    if (!this.expandedValue) this.expandedValue = true
  }

  activeTabValueChanged() {
    this.renderTabs()
  }

  renderTabs() {
    const active = this.activeTabValue
    // Four tabs: employees / trades / fleets / teams. Show the active pane, hide the rest, and
    // style the matching tab button as selected.
    const tabs = ["employees", "trades", "fleets", "teams"]
    tabs.forEach((key) => {
      const paneKey = `${key}Pane`
      const tabKey = `${key}Tab`
      const on = key === active
      if (this[`has${this.capitalize(paneKey)}Target`]) {
        this[`${paneKey}Target`].classList.toggle("hidden", !on)
      }
      if (this[`has${this.capitalize(tabKey)}Target`]) {
        this[`${tabKey}Target`].classList.toggle("bg-white/15", on)
        this[`${tabKey}Target`].classList.toggle("text-white", on)
        this[`${tabKey}Target`].classList.toggle("text-white/80", !on)
      }
    })
    // Re-sync alignment after a pane swap — the trades/fleets panes have a different width than
    // the employees grid so the panel-root width may change.
    this.scheduleSync()
  }

  capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  // Shared search across all employee pool cards (4-week view).
  filterPool() {
    this.applyPool()
  }

  // Chip quick-filters (CHIP_FILTERS): toggling a chip adds or removes its id, "Alle" clears the set.
  toggleChip(event) {
    const { filter } = event.params
    const id = event.currentTarget.dataset.chipId
    const active = this.chipFilters[filter]
    if (active.has(id)) active.delete(id)
    else active.add(id)
    this.chipFilterChanged(filter)
  }

  resetChipFilter(event) {
    const { filter } = event.params
    this.chipFilters[filter] = new Set()
    this.chipFilterChanged(filter)
  }

  chipFilterChanged(filter) {
    this.saveChipFilter(filter)
    this.syncChips(filter)
    this.applyAllFilters()
  }

  loadChipFilters() {
    this.chipFilters = {}
    Object.keys(CHIP_FILTERS).forEach((filter) => {
      this.chipFilters[filter] = this.loadChipFilter(filter)
      this.syncChips(filter)
    })
  }

  loadChipFilter(filter) {
    let stored
    try {
      stored = JSON.parse(sessionStorage.getItem(CHIP_FILTERS[filter].storageKey) || "[]")
    } catch {
      stored = []
    }
    // Drop ids whose chip no longer exists (deleted trade or type, narrowed global trade filter,
    // absent "Ohne …" chip) so a stale selection can't invisibly hide everyone.
    const rendered = new Set(this.chipTargets(filter).map((chip) => chip.dataset.chipId))
    return new Set(stored.filter((id) => rendered.has(id)))
  }

  saveChipFilter(filter) {
    try {
      sessionStorage.setItem(
        CHIP_FILTERS[filter].storageKey,
        JSON.stringify([...this.chipFilters[filter]]),
      )
    } catch {
      // sessionStorage unavailable (e.g. blocked) — the filter still works for this render.
    }
  }

  chipTargets(filter) {
    return this[`${CHIP_FILTERS[filter].target}Targets`]
  }

  // Reflect the active set on the chips: the "Alle" chip is active while no filter is set;
  // colored chips tint with their color (same hex+alpha trick as the trades pane cards).
  syncChips(filter) {
    const active = this.chipFilters[filter]
    this.chipTargets(filter).forEach((chip) => {
      const id = chip.dataset.chipId
      const on = id === "all" ? active.size === 0 : active.has(id)
      const color = chip.dataset.chipColor
      if (color) {
        chip.style.borderColor = on ? color : ""
        chip.style.backgroundColor = on ? `${color}1A` : ""
      } else {
        chip.classList.toggle("bg-gray-100", on)
        chip.classList.toggle("border-gray-400", on)
        chip.classList.toggle("bg-white", !on)
        chip.classList.toggle("border-gray-300", !on)
      }
      chip.classList.toggle("font-medium", on)
    })
  }

  matchesTradeFilter(item) {
    const active = this.chipFilters.trade
    if (active.size === 0) return true
    const ids = (item.dataset.tradeIds || "").split(/\s+/).filter(Boolean)
    if (ids.length === 0) return active.has("none")
    return ids.some((id) => active.has(id))
  }

  // A card without the attribute (no employee behind it) never hides behind the type filter.
  matchesTypeFilter(item) {
    const active = this.chipFilters.type
    if (active.size === 0) return true
    const id = item.dataset.employeeTypeId
    return id === undefined || active.has(id)
  }

  // Multi-select: tick employee cards, then drag any ticked card to plan the whole set at once
  // (the drag controller reads the [data-resource-selected] cards; the drop handler / dialog
  // create one allocation per selected employee). Selection is keyed by employee id so all of an
  // employee's cards (one per day column) stay in sync.
  toggleSelect(event) {
    const id = event.currentTarget.dataset.resourceId
    if (!id) return
    if (event.currentTarget.checked) this.selectedIds.add(id)
    else this.selectedIds.delete(id)
    this.saveSelection()
    this.syncSelection()
  }

  // A click on the checkbox must not bubble to the card (which would start a drag) or to any
  // hover overlay — let the native checkbox toggle (change->toggleSelect) handle it alone.
  stopSelectClick(event) {
    event.stopPropagation()
  }

  clearSelection(event) {
    event?.stopPropagation()
    this.selectedIds = new Set()
    this.saveSelection()
    this.syncSelection()
  }

  // Reflect the selected set onto every card (per-day columns + pool), the checkboxes, and the
  // header summary. Stale ids (cards no longer rendered) are pruned so the count can't drift.
  syncSelection() {
    if (!this.selectedIds) this.selectedIds = new Set()
    const rendered = new Set(this.selectCheckboxTargets.map((cb) => cb.dataset.resourceId))
    this.selectedIds.forEach((id) => {
      if (!rendered.has(id)) this.selectedIds.delete(id)
    })

    this.selectCheckboxTargets.forEach((cb) => {
      cb.checked = this.selectedIds.has(cb.dataset.resourceId)
    })
    this.allCards().forEach((card) => {
      const selected = this.selectedIds.has(card.dataset.resourceId)
      card.dataset.resourceSelected = selected ? "true" : "false"
      SELECTED_CLASSES.forEach((cls) => card.classList.toggle(cls, selected))
    })

    const count = this.selectedIds.size
    if (this.hasSelectionBarTarget) {
      this.selectionBarTarget.classList.toggle("hidden", count === 0)
      this.selectionBarTarget.classList.toggle("flex", count > 0)
    }
    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = `${count} ${this.countLabelValue}`.trim()
    }
  }

  allCards() {
    return [...this.itemTargets, ...this.poolItemTargets]
  }

  loadSelection() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(SELECTED_KEY) || "[]"))
    } catch {
      return new Set()
    }
  }

  saveSelection() {
    try {
      sessionStorage.setItem(SELECTED_KEY, JSON.stringify([...this.selectedIds]))
    } catch {
      // sessionStorage unavailable — selection still works for this render.
    }
  }

  // Proximity sort: reorder every card list (per-day columns and the 4-week pool) by air
  // distance between the employee's home and the target site, closest first. Toggled from
  // the site rows via a document-level event; composes with the filters above, which only
  // toggle visibility and never touch DOM order.
  onProximityEvent(event) {
    const detail = event.detail || {}
    if (this.proximitySort && this.proximitySort.siteId === detail.siteId) {
      this.resetProximitySort()
      return
    }
    if (!Number.isFinite(detail.lat) || !Number.isFinite(detail.lng)) return
    this.proximitySort = detail
    this.saveProximitySort()
    this.applyProximitySort()
    this.syncProximityUi()
    // Surface the result: open the panel on the employees tab.
    this.activeTabValue = "employees"
    if (!this.expandedValue) this.expandedValue = true
  }

  resetProximitySort(event) {
    event?.stopPropagation()
    this.proximitySort = null
    this.saveProximitySort()
    this.restoreDefaultOrder()
    this.syncProximityUi()
  }

  loadProximitySort() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(PROXIMITY_SORT_KEY) || "null")
      if (stored && Number.isFinite(stored.lat) && Number.isFinite(stored.lng)) return stored
    } catch {
      // Corrupt or unavailable storage — start without an active sort.
    }
    return null
  }

  saveProximitySort() {
    try {
      if (this.proximitySort) {
        sessionStorage.setItem(PROXIMITY_SORT_KEY, JSON.stringify(this.proximitySort))
      } else {
        sessionStorage.removeItem(PROXIMITY_SORT_KEY)
      }
    } catch {
      // sessionStorage unavailable — the sort still works for this render.
    }
  }

  applyProximitySort() {
    if (!this.proximitySort) return
    // Each employee repeats across day columns; compute their distance once.
    const distances = new Map()
    this.defaultOrders ||= new Map()
    this.cardListTargets.forEach((list) => {
      if (!this.defaultOrders.has(list)) this.defaultOrders.set(list, [...list.children])
      // Sorting the default snapshot keeps the tiebreak stable (availability, then name).
      const sorted = [...this.defaultOrders.get(list)].sort(
        (a, b) => this.cardDistance(a, distances) - this.cardDistance(b, distances),
      )
      sorted.forEach((card) => {
        list.appendChild(card)
        this.updateDistanceBadge(card, this.cardDistance(card, distances))
      })
    })
  }

  restoreDefaultOrder() {
    this.defaultOrders?.forEach((cards, list) => {
      cards.forEach((card) => {
        list.appendChild(card)
        this.updateDistanceBadge(card, null)
      })
    })
  }

  cardDistance(card, cache) {
    const id = card.dataset.resourceId
    if (cache.has(id)) return cache.get(id)
    const lat = parseFloat(card.dataset.lat)
    const lng = parseFloat(card.dataset.lng)
    const km =
      Number.isFinite(lat) && Number.isFinite(lng)
        ? haversineKm(this.proximitySort.lat, this.proximitySort.lng, lat, lng)
        : Infinity
    cache.set(id, km)
    return km
  }

  // Fill and show the card's pre-rendered badge ("12 km", "?" when not geocoded); km = null
  // hides it again. The tooltip carries the distance too (the tile size has no text room).
  updateDistanceBadge(card, km) {
    const badge = card.querySelector("[data-distance-badge]")
    if (!badge) return
    const active = km !== null
    badge.textContent = active ? (Number.isFinite(km) ? `${Math.round(km)} km` : "?") : ""
    badge.classList.toggle("hidden", !active)
    if (card.dataset.baseTitle === undefined) card.dataset.baseTitle = card.title || ""
    card.title =
      active && Number.isFinite(km)
        ? `${card.dataset.baseTitle} · ${Math.round(km)} km`
        : card.dataset.baseTitle
  }

  syncProximityUi() {
    const active = Boolean(this.proximitySort)
    if (this.hasProximityChipTarget) {
      this.proximityChipTarget.classList.toggle("hidden", !active)
      this.proximityChipTarget.classList.toggle("flex", active)
    }
    if (this.hasProximityChipNameTarget) {
      this.proximityChipNameTarget.textContent = active ? this.proximitySort.siteName : ""
    }
    document.querySelectorAll("[data-proximity-sort-button]").forEach((button) => {
      const match = active && button.dataset.siteId === this.proximitySort.siteId
      button.classList.toggle("bg-teal-50", match)
      button.classList.toggle("text-teal-700", match)
      button.classList.toggle("text-gray-400", !match)
    })
  }

  // Planned-state island: a hidden element carrying {isoDate: [employeeIds]} JSON. The mutation
  // controllers replace just this element after an allocation changes, and Stimulus re-fires
  // this callback — so the unplanned-only filter reacts to a drop/removal live, without the
  // panel itself being re-rendered (per-column search inputs and scroll positions survive).
  plannedStateTargetConnected(element) {
    let parsed
    try {
      parsed = JSON.parse(element.dataset.planned || "{}")
    } catch {
      parsed = {}
    }
    this.plannedByDate = new Map(
      Object.entries(parsed).map(([date, ids]) => [date, new Set(ids.map(String))]),
    )
    // On the initial connection this fires before connect(), which applies all filters anyway;
    // only re-apply on the post-mutation replacements (chipFilters exists by then).
    if (this.chipFilters) this.applyAllFilters()
  }

  // Employee ids already planned on the column's date, or null when the filter is off /
  // the state island isn't rendered (pool layout, timeline) — null disables the check.
  plannedSetFor(column) {
    if (!this.unplannedOnly || !this.plannedByDate) return null
    return this.plannedByDate.get(column.dataset.date) || null
  }

  // One predicate for all employee-card visibility: search AND availability AND unplanned AND
  // trade filter AND Mitarbeitertyp filter. `plannedSet` is per-column (per-date); null skips the unplanned check.
  matchesFilters(item, query, plannedSet = null) {
    const matchesSearch = query === "" || (item.dataset.searchText || "").includes(query)
    const matchesAvailable = !this.availableOnly || item.dataset.available === "true"
    const matchesUnplanned = !plannedSet || !plannedSet.has(item.dataset.resourceId)
    return (
      matchesSearch &&
      matchesAvailable &&
      matchesUnplanned &&
      this.matchesTradeFilter(item) &&
      this.matchesTypeFilter(item)
    )
  }

  // Shared-resource cards (Geteilte Ressourcen) sit inside a wrapper that also carries the
  // origin-company label, so hiding the card alone would leave the label floating. Hide the
  // wrapper when there is one; every other card is its own visibility target.
  visibilityTarget(item) {
    return item.closest("[data-card-wrapper]") || item
  }

  applyPool() {
    const query = (this.hasPoolSearchTarget ? this.poolSearchTarget.value : "").trim().toLowerCase()
    this.poolItemTargets.forEach((item) => {
      this.visibilityTarget(item).classList.toggle("hidden", !this.matchesFilters(item, query))
    })
  }

  // Re-apply every filter in whichever layout is rendered (missing targets are no-ops).
  applyAllFilters() {
    this.applyPool()
    this.columnTargets.forEach((column) => this.applyColumn(column))
  }

  // Shared search across all trade cards.
  filterTrades(event) {
    const query = (event.target.value || "").trim().toLowerCase()
    this.tradeItemTargets.forEach((item) => {
      const matches = query === "" || (item.dataset.searchText || "").includes(query)
      item.classList.toggle("hidden", !matches)
    })
  }

  // Shared search across all team cards (team name + member names).
  filterTeams(event) {
    const query = (event.target.value || "").trim().toLowerCase()
    this.teamItemTargets.forEach((item) => {
      const matches = query === "" || (item.dataset.searchText || "").includes(query)
      item.classList.toggle("hidden", !matches)
    })
  }

  // Fleet search (name/number/plate) — composes with the vehicle/machine type chips.
  filterFleets() {
    this.applyFleetFilter()
  }

  // Fleet type quick-filter chips (data-fleet-type: "all" | "vehicle" | "machine" | "attachment").
  setFleetType(event) {
    event.stopPropagation()
    this.fleetTypeFilter = event.currentTarget.dataset.fleetType || "all"
    this.fleetTypeChipTargets.forEach((chip) => {
      const on = (chip.dataset.fleetType || "all") === this.fleetTypeFilter
      chip.classList.toggle("bg-gray-200", on)
      chip.classList.toggle("font-medium", on)
    })
    this.applyFleetFilter()
  }

  applyFleetFilter() {
    const query = (this.hasFleetSearchTarget ? this.fleetSearchTarget.value : "")
      .trim()
      .toLowerCase()
    const type = this.fleetTypeFilter || "all"
    this.fleetItemTargets.forEach((item) => {
      const matchesQuery = query === "" || (item.dataset.searchText || "").includes(query)
      const matchesType = type === "all" || (item.dataset.fleetType || "") === type
      this.visibilityTarget(item).classList.toggle("hidden", !(matchesQuery && matchesType))
    })
  }

  // Restore the saved open/collapsed choice (closed by default when nothing is stored).
  restoreExpanded() {
    const stored = sessionStorage.getItem(PANEL_EXPANDED_KEY)
    if (stored === "true" || stored === "false") {
      this.expandedValue = stored === "true"
    }
  }

  expandedValueChanged() {
    sessionStorage.setItem(PANEL_EXPANDED_KEY, this.expandedValue)
    this.render()
    if (this.expandedValue) this.loadContent()
  }

  // Apply the restored open/collapsed state on connect WITHOUT animating, so a panel that was
  // left open doesn't visibly slide open from the collapsed initial markup on every load. User
  // toggles afterwards still animate (the transition classes are re-enabled next frame).
  initialRender() {
    if (!this.hasContentTarget) {
      this.render()
      return
    }
    this.contentTarget.classList.remove("transition-all", "duration-300")
    this.render()
    requestAnimationFrame(() => {
      this.contentTarget.classList.add("transition-all", "duration-300")
    })
  }

  render() {
    if (!this.hasContentTarget) return

    this.contentTarget.style.maxHeight = this.expandedValue ? "50vh" : "0px"

    if (this.hasExpandIconTarget) {
      this.expandIconTarget.classList.toggle("hidden", this.expandedValue)
    }
    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.classList.toggle("hidden", !this.expandedValue)
    }

    this.adjustSpacer()
  }

  // Per-day search: filter only the column whose search box changed.
  filterColumn(event) {
    this.applyColumn(event.target.closest('[data-planning-panel-target="column"]'))
  }

  // "Nur Verfügbare anzeigen": hide unavailable (sick / on-leave) employees everywhere.
  toggleAvailableOnly(event) {
    this.availableOnly = event.target.checked
    this.columnTargets.forEach((column) => this.applyColumn(column))
  }

  // "Nur Ungeplante anzeigen": per-day columns — hide employees already planned that day (≥1
  // allocation on any site/task), so a day's column empties as the day gets fully planned.
  toggleUnplannedOnly(event) {
    this.unplannedOnly = event.target.checked
    this.columnTargets.forEach((column) => this.applyColumn(column))
  }

  applyColumn(column) {
    if (!column) return
    const input = column.querySelector('input[type="text"]')
    const query = (input ? input.value : "").trim().toLowerCase()
    const plannedSet = this.plannedSetFor(column)

    column.querySelectorAll('[data-planning-panel-target="item"]').forEach((item) => {
      item.classList.toggle("hidden", !this.matchesFilters(item, query, plannedSet))
    })
  }
}
